<template>
  <div class="p-5">
    <div class="card-header">
      <h3 class="card-title m-0">
        <label class="card-label fw-bolder">Mapa de Navegação</label>
      </h3>
    </div>
    <div class="mt-5">
      <div v-if="loading.tree">
        <!-- Skeleton para pastas principais -->
        <div v-for="i in 2" :key="i">
          <el-skeleton style="margin-left: 10px" :rows="1" animated>
            <template #template>
              <el-skeleton-item variant="text" style="width: 300px; height: 20px" />
            </template>
          </el-skeleton>

          <!-- Skeleton para subpastas e arquivos -->
          <div v-for="j in 4" :key="`sub-${j}`" style="margin-left: 30px">
            <el-skeleton style="margin-top: 10px" :rows="1" animated>
              <template #template>
                <el-skeleton-item variant="text" style="width: 200px; height: 20px" />
              </template>
            </el-skeleton>
          </div>
        </div>

        <!-- Skeleton para itens com ícones de estrela -->
        <div
          v-for="k in 3"
          :key="`star-${k}`"
          style="margin-top: 15px; margin-left: 10px"
        >
          <el-skeleton animated>
            <template #template>
              <el-skeleton-item
                variant="circle"
                style="width: 20px; height: 20px; margin-right: 10px"
              />
              <el-skeleton-item variant="text" style="width: 120px; height: 20px" />
            </template>
          </el-skeleton>
        </div>
      </div>
      <el-tree
        v-else
        v-if="tree.length > 0"
        style="max-width: 600px"
        :props="props"
        :key="treeKey"
        @node-click="handleNodeClick"
        :load="loadNode"
        lazy
        ref="refTree"
      >
        <template #default="{ node }">
          <div>
            <i :class="`${getIconClass(node)} size-icon me-2`"></i>
            {{ node.label }}
          </div>
        </template>
      </el-tree>
      <el-empty description="Esta concessionária não possui um cardápio disponível" v-if="tree.length == 0" />
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, nextTick, watch, reactive } from "vue";
import {
  getTreeInitial,
  getVehicles,
  getPackages,
  getPackage,
  getPackageDetail,
} from "@/services/CardapioService";
import { useStore } from "vuex";
import useEmitter from "@/composables/Emmiter";
import { treeHistory } from "@/pinia/cardapio/tree/useTree";
import { filterCardapio } from "@/pinia/cardapio/filter/useFilterCardapio";
import { usePackage } from "@/pinia/cardapio/package/usePackage"

const niveisTree = {
  familia: 1,
  modeloVeiculo: 2,
  revisao: 3,
  itemRevisao: 4,
  pacote: 5,
  itemPacote: 6  
}
const tree = ref([]);
const refTree = ref();
const treeKey = ref(0);
const storeTree = ref();
const openNodes = ref([]);
const storePackage = usePackage()
const store = useStore();
const emitter = useEmitter();
const storeTreeHistory = treeHistory();
const storeFilter = filterCardapio();
const concessionariaSelected = ref(localStorage.getItem("filtroConcessionaria"));
const itemSelected = ref({});
const grupoExibicaoSelect = ref("");
const loading = reactive({
  tree: false
})

const props = ref({
  label: "name",
  isLeaf: (data) => data.isLeaf,
});

onMounted(async () => { 
  searchMenu()
});

watch(
  () => storeFilter.getCodConcessionaria,
  (newvalue) => {
    concessionariaSelected.value = newvalue;
    searchMenu();
  }
);

// emitter.on("filter-cardapio", async ({ codConcessionaria }) => {
//   concessionariaSelected.value = codConcessionaria;
//   searchMenu();
// });

emitter.on("update-family", () => {
  restoreOpenNodes();
});

emitter.on("update-tree", () => {
  restoreOpenNodes();
});

emitter.on("salve-history-tree", () => {
  saveOpenNodes();
});

const searchMenu = async () => {
  loading.tree = true
  tree.value = await getTreeInitial(concessionariaSelected.value);
  storePackage.setPackage(tree.value)
  treeKey.value++;
  loading.tree = false
};

const getIconClass = (nodeData) => {
  const { level, expanded, data } = nodeData;
  const { icon, iconExpanded, package: isPackage, service: isService } = data;

  const defaultIcon = "bi bi-file-earmark"; // default icon

  const iconRules = {
    1: () => (expanded ? iconExpanded : icon),
    2: () => (isService ? icon : expanded ? iconExpanded : icon),
    3: () => (expanded ? iconExpanded : icon),
    4: () => (isPackage ? (expanded ? iconExpanded : icon) : icon),
    5: () => icon,
  };

  return iconRules[level] ? iconRules[level]() : defaultIcon;
};

const handleNodeClick = (data) => {
  storeTreeHistory.setNode(data);
  emitter.emit("select-item", data);
};

const loadNode = async (node, resolve) => {
  const codConcessionaria = Number(concessionariaSelected.value);
  const handleError = (error) => {
    console.error("Erro ao carregar filhos:", error);
    resolve([]);
  };

  const setLeafNodes = (childNodes) => {
    childNodes.forEach((element) => {
      element.isLeaf = element.data.existChildren;
    });
  };

  if (node && node.level === 0) {
    resolve(tree.value);
    return;
  }

  let payload;
  let children;

  if (node.level === niveisTree.familia) {
    if (node && node.data && node.data.package) {
      payload = { codConcessionaria, codPackage: node.data.codPackage };
      children = await getPackageDetail(payload);
      resolve(children);
      setLeafNodes(node.childNodes);
    } else {
      payload = { codConcessionaria, family: node.data.name };
      children = await getVehicles(payload);

      resolve(children);
    }
  } else if (node.level === niveisTree.modeloVeiculo) {
    if (node && node.data && node.data.family && node.data.name) {
      payload = { codConcessionaria, vehicles: node.data.family, family: node.data.name };
      children = await getPackages(payload);
      resolve(children);
    }
  } else if (node.level === niveisTree.revisao) {
    if (
      node &&
      node.data &&
      node.data.vehicles &&
      node.data.family &&
      node.data.revisao
    ) {
      payload = {
        codConcessionaria,
        vehicles: node.data.vehicles,
        family: node.data.family,
        revisao: node.data.revisao,
        codigoFabrica: node.data.codigoFabrica,
      };

      children = await getPackage(payload);
      grupoExibicaoSelect.value = node.data.vehicles;
      resolve(children);
      setLeafNodes(node.childNodes);
    }
  } else if (node.level === niveisTree.itemRevisao && node.data.package) {
    if (node && node.data && node.data.codPackage) {
      payload = {
        codConcessionaria,
        codPackage: node.data.codPackage,
        grupoExibicao: grupoExibicaoSelect.value,
      };
      children = await getPackageDetail(payload);
      resolve(children);
      setLeafNodes(node.childNodes);
    }
  }

  await nextTick();
};

const saveOpenNodes = () => {
  if (refTree.value) {
    for (let node of Object.values(refTree.value.store.nodesMap)) {
      if (node.expanded) {
        openNodes.value.push(node.data);
      }
    }

    storeTreeHistory.setTree(openNodes.value);
  }
};

const expandNodeRecursive = async (node) => {
  if (!node) return;

  try {
    await new Promise((resolve) => loadNode(node, resolve));
  } catch (error) {
    console.error("Erro ao expandir o nó:", error);
  }
};

const restoreOpenNodes = async () => {
  if (!storeTreeHistory.getInitDate) {
    saveOpenNodes();
  }

  if (!storeTreeHistory.getInitDate) {
  }

  loading.tree = true
  tree.value = await getTreeInitial(concessionariaSelected.value);
  treeKey.value++;
  loading.tree = false

  await nextTick();

  const findNodeByName = (name) => {
    return refTree.value
      ? Object.values(refTree.value.store.nodesMap).find(
          (node) => node.data.name === name
        )
      : {};
  };

  for (let nodeName of storeTreeHistory.getHitoryTree) {
    let node = findNodeByName(nodeName.name);
    if (node) {
      node.expand();

      await nextTick();
      await expandNodeRecursive(node);
    }
  }

  if (storeTreeHistory.getInitDate) {
    emitter.emit("select-item", storeTreeHistory.handleNode);
    storeTreeHistory.setUpdate(false);
  }
};
</script>

<style>
.icon-success {
  color: #34bfa3 !important;
}

.size-icon {
  font-size: 18px;
}

.icon-danger {
  color: #f4516c !important;
}

.icon-primary {
  color: #5867dd !important;
}

.icon-primary-clear {
  color: #009ef7 !important;
}

.icon-warning {
  color: #ffb822 !important;
}

.icon-check {
  color: #00c5dc !important;
}
</style>
